import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, AlertTriangle, Edit, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
} from '@/components/common';
import { inventoryApi, InventoryItem, InventoryLevel } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

const locationTypeColors: Record<string, string> = {
  HUB: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  SITE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  DEPOT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  SUPPLIER: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

export default function InventoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [levels, setLevels] = useState<InventoryLevel[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [itemData, levelsData] = await Promise.all([
        inventoryApi.getById(id),
        inventoryApi.getLevels(id),
      ]);
      setItem(itemData);
      setLevels(levelsData);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return <LoadingState />;
  }

  if (!item) {
    return null;
  }

  const totalQuantity = item.aggregated?.totalQuantity || levels.reduce((sum, l) => sum + l.quantity, 0);
  const totalReserved = item.aggregated?.totalReserved || levels.reduce((sum, l) => sum + l.reservedQty, 0);
  const totalAvailable = item.aggregated?.totalAvailable || (totalQuantity - totalReserved);
  const totalValue = totalQuantity * (item.unitCost || 0);

  const levelColumns: Column<InventoryLevel>[] = [
    {
      key: 'location',
      header: 'Location',
      render: (level) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium">{level.location?.name || '-'}</div>
            <div className="text-sm text-muted-foreground">{level.location?.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (level) => (
        <Badge className={locationTypeColors[level.location?.type || ''] || ''}>
          {level.location?.type || '-'}
        </Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (level) => (
        <span className="font-medium">{level.quantity} {item.unit}</span>
      ),
    },
    {
      key: 'reserved',
      header: 'Reserved',
      render: (level) => (
        <span className="text-muted-foreground">{level.reservedQty} {item.unit}</span>
      ),
    },
    {
      key: 'available',
      header: 'Available',
      render: (level) => (
        <span className="font-medium text-emerald-600">{level.availableQty} {item.unit}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (level) => (
        <StatusBadge status={statusMap[level.status || 'Optimal'] || 'optimal'} />
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader
          title={item.name}
          description={`SKU: ${item.sku}`}
          breadcrumbs={[
            { label: 'Inventory', href: '/inventory' },
            { label: item.name },
          ]}
          actions={
            <div className="flex gap-2">
              <Button variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Restock
              </Button>
              <Button variant="outline">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </div>
          }
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Quantity"
          value={`${totalQuantity.toLocaleString()} ${item.unit}`}
          icon={<Package className="h-5 w-5" />}
        />
        <MetricCard
          label="Available"
          value={`${totalAvailable.toLocaleString()} ${item.unit}`}
        />
        <MetricCard
          label="Reserved"
          value={`${totalReserved.toLocaleString()} ${item.unit}`}
        />
        <MetricCard
          label="Total Value"
          value={`$${totalValue.toLocaleString()}`}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Item Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Item Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <StatusBadge status={statusMap[item.status || 'Optimal'] || 'optimal'} />
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Category</span>
              <Badge variant="secondary">{item.category}</Badge>
            </div>
            {item.subcategory && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subcategory</span>
                <span>{item.subcategory}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Min Stock Level</span>
              <span className="font-medium">{item.minStockLevel} {item.unit}</span>
            </div>
            {item.maxStockLevel && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Max Stock Level</span>
                <span className="font-medium">{item.maxStockLevel} {item.unit}</span>
              </div>
            )}
            {item.reorderPoint && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reorder Point</span>
                <span className="font-medium">{item.reorderPoint} {item.unit}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Unit Cost</span>
              <span className="font-medium">${item.unitCost?.toLocaleString() || '0'}</span>
            </div>
            {item.leadTimeDays && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lead Time</span>
                <span>{item.leadTimeDays} days</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Locations</span>
              <span className="font-medium">{levels.length}</span>
            </div>
          </CardContent>
        </Card>

        {/* Stock Alerts */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Stock by Location</CardTitle>
            <CardDescription>Inventory levels across all locations</CardDescription>
          </CardHeader>
          <CardContent>
            {levels.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                <p className="text-lg font-medium">No stock assigned</p>
                <p className="text-muted-foreground">Add this item to a location to track stock levels.</p>
              </div>
            ) : (
              <DataTable
                data={levels}
                columns={levelColumns}
                emptyTitle="No locations"
                emptyDescription="No stock assigned to any location."
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      {item.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{item.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
